// Used to display a specific marquee for a given resource type listing
// Typically this is the first resource entry or a featured resource
import { createComponent, FelaComponent } from "react-fela";
import { gutters, layout } from "~/styleguide";
import { buttons } from "~/styleguide/links";
import { expand, fluid, aspectMinHeight } from "~/styleguide/utils";
import { textStyles, themeText, themeHeadline } from "~/styleguide/typography";
import { combineRules } from "fela";
import {
    makeAutoplaySource,
    getVideoSource,
    stripSecurityAttributes
} from "~/modules/video";
import tinyColor from "tinycolor2";

// Context
import { getThemeForModule } from "~/context/theme";
import Modal from "~/components/shared/modal";
import useModal from "~/components/shared/modal/useModal";

// Components
import Visual from "~/components/shared/visual";
import WordpressLink, {
    getLinkFromRelationship
} from "~/components/global/links";
import { ContentMaxWidth } from "~/components/layout/max-width";
import AspectContainer from "~/components/shared/aspect-container";

export default function MarqueeSimple({ block, isLcpCandidate }) {
    // Pull ACF fields
    const {
        anchor_id,
        caption,
        heading,
        heading_size = "regular",
        copy,
        link,
        link_label,
        link_type,
        link_content,
        image,
        theme_colors,
        type,
        secondary_link,
        secondary_link_type,
        secondary_link_url,
        secondary_link_content,
        video_button_label,
        video_embed,
        reduce_vpadding,
    } = block;

    // Determine theme
    const theme = getThemeForModule(theme_colors);
    const textTheme = themeText(theme);
    const headlineTheme = themeHeadline(theme);
    const isCentered = type !== "left";
    const { isShowing, toggle } = useModal();

    const parsedLink = getLinkFromRelationship(link_type, link_content, link);
    const parsedSecondaryLink =
        secondary_link === "link"
            ? getLinkFromRelationship(
                  secondary_link_type,
                  secondary_link_content,
                  secondary_link_url
              )
            : null;

    return (
        <Container
            id={anchor_id}
            style={{
                background: (theme.gradient ? theme.gradient : theme.primary)
            }}
            className="module-marquee-simple"
            reducePadding={reduce_vpadding}
        >
            {!isCentered && image && (
                <ImageContainer>
                    <Visual
                        aspect={16 / 9}
                        isLcpCandidate={isLcpCandidate}
                        image={image}
                        background="contain"
                        backgroundPosition="center center"
                    />
                </ImageContainer>
            )}

            {/* Copy content */}
            <Content>
                <ContentMaxWidth
                    extend={contentWrapperStyles(isCentered)}
                    className="module-marquee-simple-content"
                >
                    <CopyContainer isCentered={isCentered}>
                        {/* Caption */}
                        {caption && (
                            <FelaComponent
                                as="h5"
                                style={combineRules(
                                    textTheme,
                                    textStyles.sidebarHeading,
                                    eyebrowStyles
                                )}
                            >
                                <span dangerouslySetInnerHTML={{ __html: caption }} />
                            </FelaComponent>
                        )}

                        {/* Heading */}
                        {heading && (
                            <MarqueeHeading
                                headingSize={heading_size}
                                extend={combineRules(
                                    headlineTheme,
                                    textStyles.heading1
                                )}
                                dangerouslySetInnerHTML={{
                                    __html: heading
                                }}
                            />
                        )}

                        {/* Copy */}
                        {copy && (
                            <Copy
                                extend={combineRules(
                                    textTheme,
                                    textStyles.paragraphLarge
                                )}
                                as="p"
                                isCentered={isCentered}
                                dangerouslySetInnerHTML={{
                                    __html: copy
                                }}
                            ></Copy>
                        )}

                        {/* CTA Button */}
                        {parsedLink.url && link_label && (
                            <WordpressLink
                                className="CTA-1"
                                layout="button"
                                url={parsedLink.url}
                                slug={parsedLink.slug}
                                size="large"
                                label={link_label}
                                extend={ctaButtonStyles}
                                theme={theme}
                            ></WordpressLink>
                        )}

                        {/* Secondary CTA link */}
                        {parsedSecondaryLink &&
                            parsedSecondaryLink.url &&
                            video_button_label && (
                                <WordpressLink
                                    className="CTA-2"
                                    layout="button"
                                    url={parsedSecondaryLink.url}
                                    slug={parsedSecondaryLink.slug}
                                    size="large"
                                    label={video_button_label}
                                    theme={theme}
                                    outline={true}
                                    backgroundColor={theme.primary}
                                    extend={combineRules(marqueeButtonStyle, {
                                        ...textTheme,
                                        whenActive: {
                                            backgroundColor:
                                                tinyColor(theme.primary)
                                                    .lighten(14.5)
                                                    .toString() + "!important"
                                        }
                                    })}
                                ></WordpressLink>
                            )}

                        {/* Video Modal Button */}
                        {secondary_link === "video" &&
                            video_embed &&
                            video_button_label && (
                                <ModalButton
                                    onClick={toggle}
                                    href={getVideoSource(video_embed)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    extend={combineRules(marqueeButtonStyle, {
                                        ...textTheme
                                    })}
                                    backgroundColor={theme.primary}
                                    colorTheme={theme}
                                    layout="button"
                                    size="large"
                                    outline={true}
                                >
                                    <span>{video_button_label}</span>
                                </ModalButton>
                            )}

                        {/* Modal portal */}
                        {secondary_link === "video" &&
                            video_embed &&
                            video_button_label && (
                                <Modal
                                    isShowing={isShowing}
                                    hide={toggle}
                                    centered={true}
                                >
                                    <AspectContainer
                                        html={stripSecurityAttributes(
                                            makeAutoplaySource(video_embed)
                                        )}
                                    ></AspectContainer>
                                </Modal>
                            )}
                    </CopyContainer>

                    {/* </Container> */}
                </ContentMaxWidth>
            </Content>
        </Container>
    );
}

const Container = createComponent(
    ({ reducePadding }) => ({
        overflow: "hidden",
        position: "relative",
        "& h2": textStyles.heading1,

        maxWidthTablet: {
            display: "flex",
            flexDirection: "column-reverse"
        },

        extend: [
          {
            condition: reducePadding === false,      // apply only when NOT reduced
            style: {
              minWidthTablet: {
                ...aspectMinHeight.parent(1440 / 400),
              },
            },
          },
          {
            condition: reducePadding === true,
            style: {
              minWidthTablet: {
                ...aspectMinHeight.parent(1440 / 300),
              },
            },
          },
        ],
    }),
    "div",
    ["id"]
);

const Content = createComponent(() => ({
    minWidthTablet: {
        ...aspectMinHeight.child
    },
    margin: "0px",
    extend: [
        fluid("paddingH", gutters.default),
        fluid("paddingV", gutters.vertical)
    ]
}));

const contentWrapperStyles = isCentered => ({
    maxWidth: layout.marqueeContentMaxWidth,
    extend: [
        {
            condition: isCentered,
            style: {
                display: "flex",
                justifyContent: "center",
                textAlign: "center"
            }
        }
    ]
});

const marqueeButtonStyle = {
    extend: [fluid("margin-left", { max: 20, min: 8 })]
};

const MarqueeHeading = createComponent(
    ({ headingSize = "regular" }) => ({
        maxWidthTabletLandscape: {
            "& br": { display: "none" }
        },

        // Support multiple H1 heading sizes
        extend: [
            {
                condition: headingSize === "regular",
                style: {
                    ...textStyles.heading1,
                }
            },
            {
                condition: headingSize === "medium",
                style: {
                    ...textStyles.heading1Medium,
                }
            },
            {
                condition: headingSize === "small",
                style: {
                    ...textStyles.heading1Small,
                }
            }
        ]
    }),
    "h1",
    ["dangerouslySetInnerHTML"]
);

const Copy = createComponent(
    ({ isCentered }) => ({
        maxWidthTablet: {
            marginH: "auto"
        },
        extend: [
            fluid("marginTop", { min: 10, max: 20 }),
            {
                condition: !isCentered,
                style: {
                    maxWidth: 375
                }
            }
        ]
    }),
    "p",
    ["dangerouslySetInnerHTML"]
);

const CopyContainer = createComponent(({ isCentered }) => ({
    maxWidthTablet: {
        textAlign: "center"
    },
    extend: [
        {
            condition: !!isCentered,
            style: {
                maxWidth: 1000
            }
        },
        {
            condition: !isCentered,
            style: {
                minWidthTablet: {
                    maxWidth: "45%"
                }
            }
        }
    ]
}));

const ImageContainer = createComponent(() => ({
    minWidthTablet: {
        ...expand(),
        width: "55%",
        left: "45%",
        overflow: "hidden",
        "& .vv-visual": {
            height: "100%"
        }
    },
    maxWidthTablet: {
        position: "relative",
        left: 0,
        width: "100%",
        "& .vv-image": {
            backgroundPosition: "left top !important"
        }
    }
}));

const ctaButtonStyles = {
    extend: [fluid("marginTop", { min: 40, max: 55 })]
};

const eyebrowStyles = {
    extend: [fluid("marginBottom", { min: 20, max: 40 })]
};

const ModalButton = createComponent(
    ({ layout, size, outline, colorTheme, backgroundColor }) => ({
        userSelect: "none",
        ...textStyles.paragraphLarge,
        whenActive: {
            backgroundColor:
                tinyColor(colorTheme.primary)
                    .lighten(14.5)
                    .toString() + "!important"
        },
        extend: [
            buttons({ layout, size, outline, colorTheme, backgroundColor })
        ]
    }),
    "a",
    ["onClick", "_target", "href", "rel"]
);
